import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { BellRing, Home, Users, CheckCircle2, Clock } from "lucide-react";

export const timeAgo = (iso) => {
  if (!iso) return "";
  const m = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 48) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
};

export const NeedsContactCard = () => {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/needs-contact").then(r => setData(r.data)).catch(() => setData({ count: 0, items: [] })); }, []);
  if (!data) return null;
  const n = data.count;
  return (
    <Card className={`p-5 border-2 ${n ? "border-[#C25E00] bg-[#FFF7ED]" : "border-emerald-300 bg-emerald-50"}`} data-testid="needs-contact-card">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <BellRing className={`w-5 h-5 ${n ? "text-[#C25E00]" : "text-emerald-700"}`} />
          <h2 className="text-lg font-bold text-slate-900" data-testid="needs-contact-count">
            {n === 0 ? "Nobody is waiting on you" : `${n} ${n === 1 ? "person needs" : "people need"} to be contacted`}
          </h2>
        </div>
        <Link to="/leads" className="text-xs font-semibold text-slate-700 underline" data-testid="needs-contact-pipeline-link">Open lead pipeline →</Link>
      </div>
      {n > 0 && (
        <ul className="mt-4 divide-y divide-orange-200/70" data-testid="needs-contact-list">
          {data.items.slice(0, 8).map(it => (
            <li key={`${it.kind}-${it.id}`}>
              <Link to={it.to} className="flex items-center gap-3 py-2.5 hover:bg-white/60 rounded px-1 -mx-1 min-h-[44px]" data-testid={`needs-contact-item-${it.id}`}>
                {it.kind === "owner_lead" ? <Home className="w-4 h-4 text-slate-500 shrink-0" /> : <Users className="w-4 h-4 text-slate-500 shrink-0" />}
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-slate-900 truncate">{it.name || it.email}</div>
                  <div className="text-[11px] text-slate-600 truncate">
                    {it.kind === "owner_lead" ? `Property owner · ${it.address || "no address"}` : `Access request · ${it.organization || it.role_type || ""}`}
                  </div>
                </div>
                {it.kind === "owner_lead" && (
                  <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded inline-flex items-center gap-1 ${it.verification_status === "verified" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>
                    {it.verification_status === "verified" ? <CheckCircle2 className="w-3 h-3" /> : <Clock className="w-3 h-3" />}{it.verification_status === "verified" ? "verified" : "unverified"}
                  </span>
                )}
                <span className="text-xs font-mono text-slate-500 shrink-0 w-14 text-right">{timeAgo(it.submitted_at)}</span>
              </Link>
            </li>
          ))}
          {data.items.length > 8 && <li className="pt-2 text-xs text-slate-600">+{data.items.length - 8} more in the pipeline</li>}
        </ul>
      )}
    </Card>
  );
};
